/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

import { QUERY_PERSIST_CACHE } from '@airasia-rn/ui';
import { flightKeys } from './flights';

const ADDONS_QUERY_PATH = [QUERY_PERSIST_CACHE, 'flights', 'addon'];
const GUEST_LIST_QUERY_PATH = [QUERY_PERSIST_CACHE, 'flights', 'guest'];

const CHECKOUT_ANCILLARY_SELECTION_KEYS = [
  ADDONS_QUERY_PATH,
  GUEST_LIST_QUERY_PATH,
] as const;

export const clearCheckoutAncillarySelections = (queryClient: QueryClient) => {
  CHECKOUT_ANCILLARY_SELECTION_KEYS.forEach((queryKey) => {
    queryClient.removeQueries({ queryKey, exact: false });
  });
  [
    flightKeys.contactDetails,
    ['flights', 'guest', 'formDraft'],
    ['flights', 'guest', 'contactFormDraft'],
    flightKeys.checkoutGuestSession,
    flightKeys.guestFormNavigationHandoff,
  ].forEach((queryKey) => {
    queryClient.removeQueries({ queryKey, exact: false });
  });
};

export const useCheckoutAncillaryReset = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    clearCheckoutAncillarySelections(queryClient);
  }, [queryClient]);
};

export const clearAddonSelections = (queryClient: QueryClient) => {
  queryClient.removeQueries({ queryKey: ADDONS_QUERY_PATH, exact: false });
};

// Clears only addon selections (baggage, meals, seat, insurance, fast pass,
// carbon offset, wheelchair, bundles, etc.), leaving guest details/selection
// untouched - for flows like refreshing fares where the same guests should
// carry over but stale addon choices for the (possibly now-different)
// fare/flight should not.
export const useAddonSelectionsReset = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    clearAddonSelections(queryClient);
  }, [queryClient]);
};
